import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { setAbout } from '../store/ui';

const AboutBoxOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;

  z-index: 1000;

  transition: all 0.3s ease;

  background-color: rgba(0, 0, 0, 0.43);
  opacity: ${(props) => (props.visible ? '1.0' : '0.0')};
  pointer-events: ${(props) => (props.visible ? 'auto' : 'none')};
`;

const AboutBoxContent = styled.div`
  position: absolute;
  z-index: 1000;
  user-select: text;



  font-family: "Source Sans Pro", sans-serif;
  font-size: 16px;
  line-height: 1.4;
  font-weight: 400;

  transition: all 0.3s ease;

  right: ${(props) => (props.visible ? '35%' : '110%')};
  width: 65%;

  @media only screen and (min-width: 850px) {
    right: ${(props) => (props.visible ? '55%' : '110%')};
    width: 45%;
  }
  
  padding: 1.5rem;

  @media only screen and (max-width: 430px) {
    right: ${(props) => (props.visible ? '15%' : '110%')};
    width: 85%;
    padding: 8%;
  }

  top: 0;
  bottom: 0;

  background-color: rgba(0, 0, 0, 0.9);
  color: white;

  h3 {
    text-transform: uppercase;
    font-size: 14px;
    letter-spacing: 2px;
    user-select: text;
  }

  p,
  ol {
    user-select: text;
    line-height: 1.45;
    max-width: 50em;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.45;
    user-select: text;
  }

  i {
    user-select: text;
    letter-spacing: 0.5px;
  }

  a {
    color: white;
    text-decoration: underline;
    user-select: text;

    &:hover {
      color: #ffd6b6;
    }
  }

  /* has to be scroll, not auto */
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
`;

const AboutBoxTitle = styled.h1`
  font-family: "Lusitana", serif;
  font-size: 28px;
  font-weight: 700;
  user-select: text;
`;

const AboutBoxLead = styled.div`
  font-family: "Lusitana", serif;
  user-select: text;
  font-size: 22px;
  line-height: 1.5;
  font-weight: 700;

  margin-top: 2rem;
  margin-bottom: 2rem;
`;

const AboutBoxSubTitle = styled.h2`
  font-family: "Lusitana", serif;
  user-select: text;
  font-size: 22px;
  font-weight: 700;
  padding-top: 0.75em;
`;

const AboutBoxClose = styled.div`
  position: absolute;
  right: 1.25rem;
  top: 1.25rem;
  width: 2.5rem;
  height: 2.5rem;

  opacity: 0.8;

  &:hover {
    opacity: 1;
  }

  &:before,
  &:after {
    position: absolute;
    left: 1rem;
    content: " ";
    height: 2.5rem;
    width: 1px;
    background-color: white;
  }

  &:before {
    transform: rotate(45deg);
  }

  &:after {
    transform: rotate(-45deg);
  }
`;

/**
 * AboutBox React Component.
 */
class AboutBox extends PureComponent {
  static propTypes = {
    isVisible: PropTypes.bool.isRequired,
    hideAbout: PropTypes.func.isRequired,
  };

  /**
   * @param {object} props Properties.
   */
  constructor(props) {
    super(props);

    this.aboutBoxContent = React.createRef();
  }

  /**
   * React lifecycle handler.
   */
  componentDidUpdate() {
    if (this.aboutBoxContent.current) {
      this.aboutBoxContent.current.scrollTop = 0;
    }
  }

  /**
   * React lifecycle handler.
   * @return {jsx}
   */
  render() {
    const { isVisible, hideAbout } = this.props;

    return (
      <div>
        <AboutBoxOverlay visible={isVisible} onClick={hideAbout} />
        <AboutBoxContent visible={isVisible} ref={this.aboutBoxContent}>
          <AboutBoxClose onClick={hideAbout} />
          <AboutBoxTitle>Coastline Paradox</AboutBoxTitle>
          <AboutBoxLead>
            A project by Pekka Niittyvirta and Timo Aho in collaboration with
            {' '}
            <a
              href="https://artsandculture.google.com/"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Google Arts &amp; Culture.
            </a>
          </AboutBoxLead>
          <p>
            <i>Coastline Paradox</i>
            &nbsp;
            is an interactive online art experiment by Finnish artists Pekka
            Niittyvirta and Timo Aho. The project visualizes future sea level
            rise and simulates the migration caused by the climate crisis.
          </p>
          <p>
            The experiment engages the viewer with the impacts of the climate
            crisis through a custom built map interface and Google Street View.
            The glowing areas on the map interface and the glowing horizontal
            line on the Street View represents the sea level rise. These can be
            altered through an interactive parameter – time, which visualizes
            the impact of this man-made environmental disaster.
          </p>
          <p>
            Coastline Paradox is a continuation from the artists’ previous
            installation Lines (57° 59′ N, 7° 16’W), which was first opened in
            May Day 2018 at
            {' '}
            <a
              href="https://www.taigh-chearsabhagh.org/events/lines-57-59-n-7-16w/"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Taigh Chearsabhagh Art Center
            </a>
            {' '}
            in the Outer Hebridean island of North Uist, Scotland. Covering a
            total length of 115 m horizontal lines of lights installed in three
            locations in the vicinity of the art centre. The installation is
            activated by the rising tide showing where the future storm surges
            will likely rise within this century.
          </p>
          <p>
            By extending their work to a global online platform, the artists
            invite the viewer to explore a world where sea levels rise and
            instability increases the further we advance into our uncertain
            future.
          </p>

          <AboutBoxSubTitle>Heartbeat of the Earth</AboutBoxSubTitle>

          <p>
            Coastline Paradox is part of
            {' '}
            <a
              href="http://g.co/heartbeatoftheearth"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Heartbeat Of The Earth
            </a>
            , a series of online climate artworks.
          </p>

          <AboutBoxSubTitle>Credits</AboutBoxSubTitle>

          <p>
            Concept:&nbsp;
            <a
              href="http://www.niittyvirta.com/"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Pekka Niittyvirta
            </a>
            {' '}
            &amp;&nbsp;
            <a
              href="http://timoaho.org/"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Timo Aho
            </a>
            .
          </p>

          <p>
            Data &amp; design:
            <br />
            Jonatan Hildén &amp; Juuso Koponen -&nbsp;
            <a
              href="http://koponen-hilden.fi/"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Koponen+Hildén
            </a>
          </p>

          <p>
            Code:&nbsp;
            <br />
            Map:&nbsp;
            <a
              href="http://viljamipeltola.com"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Viljami Peltola
            </a>
            <br />
            Street View:&nbsp;
            <a
              href="https://viiksimaisteri.se"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Ville Niemi
            </a>
            ,&nbsp;
            <a
              href="http://www.simplicitydesign.fi/"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Janne Aukia
            </a>
            <br />
            Backend/Python & essential work + handyman:&nbsp;
            <a
              href="https://twitter.com/spheroid"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Henry Jalonen
            </a>
          </p>

          <AboutBoxSubTitle>Background</AboutBoxSubTitle>
          <h3>Map view</h3>
          <p>
            The map view shows land areas inundated by sea level rise based on
            digital elevation data from
            {' '}
            <a
              href="https://go.climatecentral.org/coastaldem/"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Climate Central’s CoastalDEM elevation model
            </a>
            {' '}
            and NASA. The Earth is divided into a grid of cells that each have
            an altitude in meters. Cells with an altitude below the current sea
            level in the visualization are displayed in glowing white. Because
            of inaccuracies in the elevation data, which is from measurements
            by instruments in Earth’s orbit, some places can appear as being
            lower or higher in altitude than they actually are.
          </p>
          <h3>Street view</h3>
          <p>
            In Street View mode the viewer experiences how the sea level could
            rise at a particular location. The altitude data for the map view
            and the street view is not exactly the same for all locations, so
            some mismatches may occur. A location that on the map is displayed
            as being under water at a given time can appear unaffected by sea
            level rise in street view – or vice versa.
          </p>
          <h3>Sea level rise and global warming</h3>
          <p>
            How the sea level might rise in the future is based on values
            published by the Intergovernmental Panel on Climate Change’s (IPCC)
            Special Report on the Ocean and Cryosphere in a Changing Climate
            (2019). These values can be seen as the current scientific
            consensus on the sea level rise that global warming could cause.
          </p>

          <p>
            The sea level rise is calculated relative to the so-called RCP8.5
            scenario, which represents very high and rising emissions of CO₂
            and other greenhouse gases by humans. The RCPs or Representative
            Concentration Pathways are different scenarios devised from climate
            models by the IPCC. The different RCPs try to provide plausible
            predictions of how emissions and atmospheric greenhouse gas
            concentrations might develop over time. Read more about climate
            models here:
            {' '}
            <a
              href="https://www.carbonbrief.org/qa-how-do-climate-models-work"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              https://www.carbonbrief.org/qa-how-do-climate-models-work
            </a>
          </p>
          <h3>Population figures</h3>
          <p>
            Population figures come from a dataset called
            {' '}
            <a
              href="https://sedac.ciesin.columbia.edu/data/set/gpw-v4-population-count-adjusted-to-2015-unwpp-country-totals-rev11/data-download"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Gridded Population of the World
            </a>
            {' '}
            where the world’s population estimated for the year 2020 is
            assigned to a grid of roughly 1×1 kilometer squares. If a grid
            square on screen is in an area affected by sea level rise, its
            population is counted as having to relocate. As this does not
            account for how population might develop in the future, these
            figures are only very roughly indicative. In street view mode the
            population corresponding to the current location is shown.
          </p>
          <h3>Temperature and CO₂</h3>
          <p>
            The temperature shows the so-called global temperature anomaly,
            which tells how much the Earth on average has warmed since
            pre-industrial times. The CO₂ concentration value is the amount of
            carbon dioxide in the atmosphere in parts per million. Both are
            global median figures. The actual warming of different places on
            Earth will vary considerably from global values, with for instance
            locations near the Arctic warming much more. Contextual texts
            describe some of the effects of increasing global temperatures.
          </p>
          <h3>Uncertainties and caveats</h3>
          <p>
            All values in the visualization are from modelled projections, not
            observed data. Since the actual development of Earth’s climate
            system and human-caused emissions do not exactly follow the climate
            models, measured values since the year 2000 for sea level rise,
            temperature and CO₂ concentrations are not necessarily the same as
            the values used in the visualization.
          </p>

          <p>
            Viewers should furthermore notice that the visualized projections
            hundreds of years into the future, while based on the actual
            climate models used by the IPCC, are fundamentally extremely
            uncertain. The underlying variation in the models is large and
            grows when projected past 2100.
          </p>

          <p>
            The models used here also do not account for possible future
            actions by humanity or direct and indirect effects from climate
            change on societies that might affect how emissions develop.
          </p>
          <h3>Collapse of ice sheets</h3>
          <p>
            By dragging the slider past the year 2300, the viewer can move
            outside the predicted scenarios and explore sea level rise all the
            way to the complete loss of all land ice, leading to a sea level
            approximately 70 meters higher than today. The rate at which this
            happens would depend on at what level the global temperature
            stabilizes, but it would likely take thousands of years.
          </p>

          <p>
            However, with a global temperature anomaly above 2°C the collapse
            of major ice sheets on Greenland, West Antarctica and part of East
            Antarctica could already be locked in, which would commit the world
            to around 10 metres of irreversible sea-level rise in the much
            shorter time-frame of around 1,000 years.
          </p>

          <AboutBoxSubTitle>Scientific advisor’s commentary</AboutBoxSubTitle>
          <AboutBoxSubTitle>Jouni A. Räisänen, PhD</AboutBoxSubTitle>
          <p>
            I have seen the Coastline Paradox visualization tool and read the
            associated explanatory texts. I find the scientific basis of the
            tool consistent with the present understanding. It seems currently
            inevitable that the ongoing increase in global sea level will
            continue, probably for several centuries into the future. While
            there is large uncertainty in the magnitude of the change,
            particularly for time horizons exceeding a few decades and at
            regional scales, these uncertainties are appropriately discussed in
            the explanatory texts. It is also important to realize that the
            magnitude of future sea level rise will strongly depend on the
            actions of mankind. Larger greenhouse gas emissions lead to a
            larger sea level rise. Conversely, if mankind succeeds to reduce
            greenhouse gas emissions well below the high-end RCP8.5 scenario
            that is used for illustration, the increase in sea level will also
            most likely be smaller.
          </p>

          <p />

          <p>
            <a
              href="https://researchportal.helsinki.fi/en/persons/jouni-r%C3%A4is%C3%A4nen"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Jouni A. Räisänen
            </a>
            <br />
            University Lecturer,
            <br />
            Institute for Atmospheric and Earth System Research (INAR)
            <br />
            Supervisor for doctoral programme,
            <br />
            Doctoral Programme in Atmospheric Sciences University of Helsinki
          </p>

          <AboutBoxSubTitle>Mikael Hildén, PhD</AboutBoxSubTitle>

          <h3> Assessment of the Coastline Paradox visualization tool</h3>
          <p>
            I have had the opportunity to test the Coastline Paradox
            visualization tool and read the associated explanatory texts. The
            science that has been used in developing the tool is based on
            current state of the art research that has been reviewed in the
            IPCC Special Report on the Ocean and Cryosphere in a Changing
            Climate

            (
            <a
              href="https://www.ipcc.ch/srocc/"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              https://www.ipcc.ch/srocc/
            </a>
            ).

            The explanatory text gives a correct view of the key results of the
            IPCC report. A central conclusion is that the ongoing increase in
            global sea level is likely to continue, probably for several
            centuries into the future. The uncertainties are appropriately
            discussed in the explanatory text. The text also correctly notes
            that the consequences of sea level rise depend on human action, not
            only in terms of control or lack of control of greenhouse gas
            emissions, but also in relation to demographic and other factors.
            The tool conveys the scientifically agreed basic message that a
            nearly unchecked sea level rise in line with the high end emission
            scenario (RCP8.5), would have considerable consequences in the
            coastal areas. The accompanying text correctly notes that the
            RCP4.5, which reflects a more moderate emission pathway would have
            lesser impacts on the sea level. In conclusion I note that the
            Coastline Paradox visualization tool gives its user a correct view
            of current scientific thinking of sea level rise, although the
            choice of scenario reflects a rather pessimistic view of the global
            efforts to limit greenhouse gas emissions. In a work of art that
            serves to illustrate possible long-term consequences this is
            permissible, especially since the explanatory text explains the
            choice and its base.
          </p>

          <p>
            <a
              href="https://www.researchgate.net/profile/Mikael_Hilden"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Mikael Hildén
            </a>
            ,
            <br />
            PhD Professor
            <br />
            Climate Change Programme,
            <br />
            <a
              href="https://www.syke.fi/en-US"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Finnish Environment Institute
            </a>
          </p>

          <AboutBoxSubTitle>Technical background</AboutBoxSubTitle>

          <h3>
            Data sources, assumptions and parameters of the visualization
          </h3>
          <p><i>Elevation data</i></p>
          <p>
            The map view displays land areas inundated by sea level rise based
            on digital elevation data from the
            {' '}
            <a
              href="https://go.climatecentral.org/coastaldem/"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              CoastalDEM90®
            </a>
            {' '}
            (v1.1.) dataset by Climate Central (Kulp &amp; Strauss, 2018). It
            covers regions 56°S to 60°N and altitudes up to 20 meters. It’s
            resolution is 3 arc seconds (about 90 meters).  For areas not
            covered by CoastalDEM the Global Multi-resolution Terrain Elevation
            Data 2010 (GMTED2010) with a resolution of 7,5 arc seconds (~225m)
            is used.
          </p>

          <p>
            CoastalDEM is based on the Shuttle Radar Topography Mission dataset
            (SRTM V3, Farr et al. 2007), which is created from radar data
            collected during a NASA space shuttle mission in 2000. The original
            SRTM data has consistent errors especially at low elevations due to
            effects of buildings, vegetation, landforms and noise.  Especially
            coastal areas with dense, tall vegetation or buildings are likely
            to have higher than actual values for altitude. The CoastalDEM
            dataset was produced using machine learning techniques to estimate
            and correct these errors. In comparison with high-accuracy lidar
            data the bias of the original SRTM data is 3.67 m for USA, while
            CoastalDEM achieves a bias lower than 0.01 m. Similar improvements
            are expected elsewhere.
          </p>

          <p>
            This visualization does not identify areas of connectivity – a
            region lower than the currently displayed sea level will be shown
            as under water despite not necessarily being in direct connection
            with the sea. Note that some isolated areas below sea level are at
            risk of flooding by water seeping through the ground.
          </p>

          <p>
            In the Street View mode the project uses CoastalDEM elevations to
            draw lines at an altitude in the panoramic scene corresponding to
            estimated sea level rise at that location and the given years in
            the future. The digital elevation data provided by Google Earth
            (EGM96 geoid) is used as a fallback for places where CoastalDEM
            gives too high values. For some locations faulty elevation values
            have also been corrected for Street View. Variation in the accuracy
            of the elevation data can cause some mismatches with the imagery.
          </p>

          <p>
            As not every location uses the same elevation data for the map view
            and the street view, some mismatch can be observed. Thus a location
            that on the map is displayed as being under water at a given time
            might show as being above sea level in street view or vice versa.
          </p>
          <p><i>Sea level rise and climate data</i></p>
          <p>
            The values given for projected sea-level rise are based on data
            from Chapter 4 of the Intergovernmental Panel on Climate Change’s
            (IPCC) Special Report on the Ocean and Cryosphere in a Changing
            Climate (2019).
          </p>

          <p>
            Values for years not included in the data tables are linearly
            interpolated. The ”High” estimates are based on the highest
            projection of greenhouse gas concentration, RCP 8.5 (Representative
            Concentration Pathway), from the fifth IPCC Assessment Report (AR5),
            which represents a rising trend in future emissions.
          </p>

          <p>
            While the knowledge of processes involved in sea level rise due to
            global warming has increased considerably, large uncertainties in
            predicting future sea level rise remain — especially concerning the
            contribution from melting ice sheets. Extreme rise in sea levels
            exceeding 2 meters by 2100 cannot be ruled out, and should thus
            inform adaptation strategies. Based on this, the default sea level
            rise values visualized are here the upper values from the 95%
            credible interval range of the ”High” estimates given by the IPCC.
          </p>

          <p>
            The visualization does not account for storm surges, tides or other
            temporary or local variations in sea levels.
          </p>

          <p>
            The global temperature anomaly and CO₂ concentration values are are
            global median figures given based on estimates calculated with
            default parameters for RCP 8.5 using the MAGICC6 modelling
            software. The reference year used for preindustrial times is 1750.
            MAGICC6 is a simplified climate model that can run on an ordinary
            desktop computer. It emulates and is calibrated against the larger,
            more complex climate models. Read more here:
            {' '}
            <a
              href="http://wiki.magicc.org/index.php?title=Model_Description"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              http://wiki.magicc.org/index.php?title=Model_Description
            </a>

          </p>

          <p>
            The actual warming experienced by different locations on Earth is
            not simulated in this project and vary considerably from the global
            means, with for instance locations near the Arctic warming much
            more. For localized projections of warming up to 2100, see:
            {' '}
            <a
              href="http://wiki.magicc.org/index.php?title=Model_Description"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              https://www.carbonbrief.org/mapped-how-every-part-of-the-world-has-warmed-and-could-continue-to-warm
            </a>
          </p>
          <p><i>Population data</i></p>
          <p>
            Population figures are derived from the
            {' '}
            <a
              href="https://sedac.ciesin.columbia.edu/data/set/gpw-v4-population-count-adjusted-to-2015-unwpp-country-totals-rev11/data-download"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              Gridded Population of World Version 4 (GPWv4)
            </a>
            {' '}
            dataset, which gives global population estimates projected for 2020
            in a 30 arc second grid (1 km). The “population affected” is
            calculated by seeing whether the location of population grid
            squares on screen become submerged by sea level rise, based on the
            elevation data. The population figures are only very roughly
            indicative as it does not account for population changes in the
            future, and as there are inaccuracies in the base data.
          </p>

          <AboutBoxSubTitle>Disclaimer</AboutBoxSubTitle>

          <p>
            Every reasonable effort has been made to make this web experiment
            as accurate as possible. This web experiment is an expression by
            the artist to generate discussion, awareness and interest in
            Climate Change, the data behind it, and consequences of human
            inaction. Coastline Paradox is not intended as a validated source
            of scientific discoveries or processes.
          </p>

          <AboutBoxSubTitle>Privacy &amp; terms</AboutBoxSubTitle>

          <p>
            <a
              href="https://policies.google.com/privacy"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              https://policies.google.com/privacy
            </a>
          </p>

          <AboutBoxSubTitle>Sources</AboutBoxSubTitle>

          <p>
            Aisch, Gregor (2019): “What different degrees of global warming
            look like”.
            {' '}
            <i>Chartable: a blog by Datawrapper</i>
            .
            {' '}
            <a
              href="hhttps://blog.datawrapper.de/climate-crisis-global-warming/"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              https://blog.datawrapper.de/climate-crisis-global-warming/
            </a>
            {' '}
            Accessed 2/2020.
          </p>

          <p>
            Bamber, Jonathan L., Oppenheimer, Michael, Kopp, Robert E.,
            Aspinall, Willy P., Cooke, Roger M. (2019). “Ice sheet
            contributions to future sea-level rise from structured expert
            judgment”,
            {' '}
            <i>Proceedings of the National Academy of Sciences</i>
            {' '}
            Jun 2019, 116 (23) 11195-11200;
            {' '}
            <a
              href="https://doi.org/10.1073/pnas.1817205116"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              https://doi.org/10.1073/pnas.1817205116
            </a>
          </p>

          <p>
            IPCC (2019). “Chapter 4: Sea Level Rise and Implications for Low
            Lying Islands, Coasts and Communities”. In:
            {' '}
            <i>
              IPCC Special Report on the Ocean and Cryosphere in a Changing
              Climate
            </i>
            {' '}
            [H.-O. Pörtner, D.C. Roberts, V. Masson-Delmotte, P. Zhai, M.
            Tignor, E. Poloczanska, K. Mintenbeck, M. Nicolai, A. Okem, J.
            Petzold, B. Rama, N. Weyer (eds.)].
            {' '}
            <a
              href="https://www.ipcc.ch/srocc/download-report/"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              https://www.ipcc.ch/srocc/download-report/
            </a>
          </p>

          <p>
            IPCC (2019). “The standard set of scenarios used in the AR5 is
            called Representative Concentration Pathways (RCPs, Box 2.2)”. In:
            {' '}
            <i>Climate Change 2014 Synthesis Report Fifth Assessment Report</i>
            .
            {' '}
            <a
              href="https://ar5-syr.ipcc.ch/index.php"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              https://ar5-syr.ipcc.ch/index.php
            </a>
          </p>
          <p>
            IPCC (2018): Summary for Policymakers. In:
            {' '}
            <i>
              Global Warming of 1.5°C. An IPCC Special Report on the impacts of
              global warming of 1.5°C above pre-industrial levels and related
              global greenhouse gas emission pathways, in the context of
              strengthening the global response to the threat of climate
              change, sustainable development, and efforts to eradicate poverty
            </i>
            {' '}
            <a
              href="https://www.ipcc.ch/sr15/chapter/spm/"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              https://www.ipcc.ch/sr15/chapter/spm/
            </a>
          </p>

          <p>
            Kulp, S. A. &amp; Strauss, B. H. (2018). CoastalDEM: A global
            coastal digital elevation model improved from SRTM using a neural
            network.
            {' '}
            <i>Remote Sensing of Environment</i>
            , 206, 231–239.
            {' '}
            <a
              href="https://doi.org/10.1016/J.RSE.2017.12.026"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              https://doi.org/10.1016/J.RSE.2017.12.026
            </a>
          </p>

          <p>
            Lenton, T. M., et. al (2019) ”Comment: Climate tipping points—too
            risky to bet against”,
            {' '}
            <i>Nature</i>
            . DOI: 10.1038/d41586-019-03595-0,
            {' '}
            <a
              href="https://nature.com/articles/d41586-019-03595-0"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              https://nature.com/articles/d41586-019-03595-0
            </a>
          </p>

          <p>
            Farr, T. G., et al. ( 2007). “The Shuttle Radar Topography Mission”,
            {' '}
            <i>Rev. Geophys.</i>
            ,45, RG2004,
            {' '}
            <a
              href="https://doi.org/10.1029/2005RG000183"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              https://doi.org/10.1029/2005RG000183
            </a>
          </p>

          <p>
            Meinshausen, M., S. C. B. Raper and T. M. L. Wigley (2011).
            “Emulating coupled atmosphere-ocean and carbon cycle models with a
            simpler model, MAGICC6: Part I – Model Description and Calibration.”
            {' '}
            <i>Atmospheric Chemistry and Physics 11</i>
            : 1417-1456.
            {' '}
            <a
              href="https://doi.org/10.5194/acp-11-1417-2011"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              https://doi.org/10.5194/acp-11-1417-2011
            </a>
          </p>

          <p>
            Center for International Earth Science Information Network - CIESIN
            - Columbia University. (2018). Gridded Population of the World,
            Version 4 (GPWv4): Population Count, Revision 11. Palisades, NY:
            NASA Socioeconomic Data and Applications Center (SEDAC).
            {' '}
            <a
              href="https://doi.org/10.7927/H4JW8BX5"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              https://doi.org/10.7927/H4JW8BX5.
            </a>
            {' '}
            Accessed 10/2019.
          </p>

          <p>
            U.S. Geological Survey (2010). Global Multi-resolution Terrain
            Elevation Data 2010.
            {' '}
            <a
              href="https://doi.org/10.5066/F7J38R2N"
              target="_BLANK"
              rel="noopener noreferrer"
            >
              https://doi.org/10.5066/F7J38R2N
            </a>
          </p>
        </AboutBoxContent>
      </div>
    );
  }
}

export default connect(
    ({ ui }) => ({ isVisible: ui.isAbout }),
    (dispatch) => ({ hideAbout: () => dispatch(setAbout(false)) }),
)(AboutBox);
